import React, { useCallback, useMemo } from 'react'
import {
  Unstable_NumberInput as BaseNumberInput,
} from '@mui/base/Unstable_NumberInput'
import { makeStyles } from '@mui/styles'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import ThemeInterface from '@/theme-interface'
import QuantityActionButton from './quantity-action-button'
import clsx from 'clsx'

const useStyles = makeStyles<ThemeInterface>((theme) => ({
  inputRoot: {
    fontWeight: 400,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  input: {
    height: 48,
    fontFamily: 'inherit',
    fontWeight: 400,
    outline: 0,
    minWidth: 0,
    background: theme.backgroundColor,
    border: 'none',
    borderTop: theme.border,
    borderBottom: theme.border,
    textAlign: 'center',
    color: theme.text,
    '&:focus-visible': {
      outline: 0,
    },
  },
}))

interface IQuantityInput {
  min?: number
  max?: number
  defaultValue?: number
  // controlled mode - when set, the input always shows this value and only reports changes
  value?: number
  disabled?: boolean
  className?: string
  formatValue?: (value: number) => string
  onChange?: (value: number) => void
}

// Define proper types for slot components
interface SlotProps {
  className?: string
  [key: string]: unknown
}

interface InputSlotProps extends SlotProps {
  value?: string
  formatValue?: (value: number) => string
}

// Memoized slot components to prevent recreation on every render
const RootSlot = React.memo(({ className, ...other }: SlotProps) => {
  const classes = useStyles()
  return <div className={clsx(classes.inputRoot, className)} {...other} />
})

const InputSlot = React.memo(({ className, formatValue, value, ...other }: InputSlotProps) => {
  const classes = useStyles()
  if (!formatValue) {
    return <input className={clsx(classes.input, className)} value={value} {...other} />
  }

  return <input className={clsx(classes.input, className)} {...other} value={formatValue(Number(value))} readOnly />
})

const IncrementButtonSlot = React.memo((props: SlotProps) => (
  <QuantityActionButton {...props} increment />
))

const DecrementButtonSlot = React.memo(QuantityActionButton)

const QuantityInput = React.memo(function QuantityInput(props: IQuantityInput) {
  const { min = 1, max = 99, defaultValue = 1, value, disabled, formatValue, onChange, className } = props

  // Memoize the onChange callback to prevent unnecessary re-renders
  const handleChange = useCallback((_: unknown, value: number) => {
    onChange?.(value)
  }, [onChange])

  // Memoize slot props to prevent recreation on every render
  const slotProps = useMemo(() => ({
    root: {
      className,
    },
    incrementButton: {
      children: <AddIcon fontSize='small' />,
      className: 'increment',
    },
    decrementButton: {
      children: <RemoveIcon fontSize='small' />,
    },
    input: {
      formatValue,
    },
  }), [formatValue, className])

  // Memoize slots to prevent recreation on every render
  const slots = useMemo(() => ({
    root: RootSlot,
    input: InputSlot,
    incrementButton: IncrementButtonSlot,
    decrementButton: DecrementButtonSlot,
  }), [])

  return (
    <BaseNumberInput
      slots={slots}
      slotProps={slotProps}
      min={min}
      max={max}
      {...(value === undefined ? { defaultValue } : { value })}
      disabled={disabled}
      onChange={handleChange}
    />
  )
})

export default QuantityInput
